import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Autocomplete, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import { fetchRawStockCodes } from '../../api/rawStockCode/rawStockCodeApi';
import { fetchStocks } from '../../api/stock/stockApi';
import { fetchPurities } from '../../api/purity/purityApi';
import { fetchColors } from '../../api/color/colorApi';

const GoldStockAddedReportFilter = ({ open, onClose, onApply, initialFilters }) => {
  const [filters, setFilters] = useState(initialFilters || {});
  
  // Master data for dropdowns
  const [stockCodes, setStockCodes] = useState([]);
  const [stockTypes, setStockTypes] = useState([]);
  const [purities, setPurities] = useState([]);
  const [colors, setColors] = useState([]);

  useEffect(() => {
    // Load master data when component mounts
    fetchRawStockCodes().then(res => setStockCodes(res.data)).catch(() => setStockCodes([]));
    fetchStocks().then(res => setStockTypes(res.data)).catch(() => setStockTypes([]));
    fetchPurities().then(res => setPurities(res.data)).catch(() => setPurities([]));
    fetchColors().then(res => setColors(res.data)).catch(() => setColors([]));
  }, []);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApply = () => {
    onApply(filters);
  };

  const handleReset = () => {
    setFilters({});
    onApply({});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Filter Gold Stock Added Report</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
            <TextField
              label="Date From"
              name="date_from"
              type="date"
              value={filters.date_from || ''}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
            <TextField
              label="Date To"
              name="date_to"
              type="date"
              value={filters.date_to || ''}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
            <Autocomplete
              options={stockCodes}
              getOptionLabel={(option) => `${option.stock_code} - ${option.stock_name || 'N/A'}`}
              value={stockCodes.find(sc => sc.stock_code === filters.stock_code) || null}
              onChange={(event, newValue) => {
                setFilters({ ...filters, stock_code: newValue ? newValue.stock_code : '' });
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Stock Code" 
                  variant="outlined"
                  placeholder="Select stock code..."
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
            <TextField
              label="Stock Name"
              name="stock_name"
              value={filters.stock_name || ''}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              placeholder="Enter stock name..."
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
            <Autocomplete
              options={stockTypes}
              getOptionLabel={(option) => option.name || option.stock_type || 'N/A'}
              value={stockTypes.find(s => s.name === filters.stock_type || s.stock_type === filters.stock_type) || null}
              onChange={(event, newValue) => {
                setFilters({ ...filters, stock_type: newValue ? (newValue.name || newValue.stock_type) : '' });
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Stock Type" 
                  variant="outlined"
                  placeholder="Select stock type..."
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
            <Autocomplete
              options={purities}
              getOptionLabel={(option) => option.name || option.purity || 'N/A'}
              value={purities.find(p => p.name === filters.purity || p.purity === filters.purity) || null}
              onChange={(event, newValue) => {
                setFilters({ ...filters, purity: newValue ? (newValue.name || newValue.purity) : '' });
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Purity" 
                  variant="outlined"
                  placeholder="Select purity..."
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ minWidth: 220 }}>
            <Autocomplete
              options={colors}
              getOptionLabel={(option) => option.name || option.color || 'N/A'}
              value={colors.find(c => c.name === filters.color || c.color === filters.color) || null}
              onChange={(event, newValue) => {
                setFilters({ ...filters, color: newValue ? (newValue.name || newValue.color) : '' });
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Color" 
                  variant="outlined"
                  placeholder="Select color..."
                />
              )}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          variant="outlined" 
          onClick={handleReset}
          startIcon={<ClearIcon />}
        >
          Clear All
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained">Apply</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoldStockAddedReportFilter; 